// IndexedDB utility for offline storage
const DB_NAME = 'snd-vms-offline';
const DB_VERSION = 2; // v2: blobs moved to separate store

const STORES = {
  OFFLINE_QUEUE: 'offlineQueue',
  CACHED_DATA: 'cachedData',
  PENDING_RECORDINGS: 'pendingRecordings', // metadata only — no blobs
  PENDING_BLOBS: 'pendingBlobs',           // blob data only
};

let db = null;

export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) { resolve(db); return; }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database');
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      if (!database.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const s = database.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id', autoIncrement: true });
        s.createIndex('timestamp', 'timestamp', { unique: false });
        s.createIndex('type', 'type', { unique: false });
      }

      if (!database.objectStoreNames.contains(STORES.CACHED_DATA)) {
        const s = database.createObjectStore(STORES.CACHED_DATA, { keyPath: 'key' });
        s.createIndex('timestamp', 'timestamp', { unique: false });
      }

      if (!database.objectStoreNames.contains(STORES.PENDING_RECORDINGS)) {
        const s = database.createObjectStore(STORES.PENDING_RECORDINGS, { keyPath: 'id', autoIncrement: true });
        s.createIndex('trackingId', 'trackingId', { unique: false });
        s.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // v2: separate store for blobs only
      if (!database.objectStoreNames.contains(STORES.PENDING_BLOBS)) {
        database.createObjectStore(STORES.PENDING_BLOBS, { keyPath: 'recordingId' });
      }

      console.log('[OfflineDB] Schema v2 ready');
    };
  });
};

// ─── Offline Queue ────────────────────────────────────────────────────────────

export const addToOfflineQueue = async (item) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = db.transaction([STORES.OFFLINE_QUEUE], 'readwrite').objectStore(STORES.OFFLINE_QUEUE);
    const req = store.add({ ...item, timestamp: Date.now(), synced: false });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

export const getOfflineQueue = async () => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction([STORES.OFFLINE_QUEUE], 'readonly').objectStore(STORES.OFFLINE_QUEUE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

export const removeFromOfflineQueue = async (id) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction([STORES.OFFLINE_QUEUE], 'readwrite').objectStore(STORES.OFFLINE_QUEUE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

export const clearOfflineQueue = async () => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction([STORES.OFFLINE_QUEUE], 'readwrite').objectStore(STORES.OFFLINE_QUEUE).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

// ─── Pending Recordings ───────────────────────────────────────────────────────

// Save metadata to pendingRecordings + blob to pendingBlobs (separate stores)
export const savePendingRecording = async (recording) => {
  await initDB();

  const { videoBlob, ...metadata } = recording;

  // 1. Save metadata (no blob)
  const recordingId = await new Promise((resolve, reject) => {
    const req = db
      .transaction([STORES.PENDING_RECORDINGS], 'readwrite')
      .objectStore(STORES.PENDING_RECORDINGS)
      .add({ ...metadata, timestamp: Date.now(), synced: false });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  // 2. Save blob separately
  await new Promise((resolve, reject) => {
    const req = db
      .transaction([STORES.PENDING_BLOBS], 'readwrite')
      .objectStore(STORES.PENDING_BLOBS)
      .put({ recordingId, videoBlob });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });

  console.log('[OfflineDB] Saved pending recording:', recording.trackingId, 'id:', recordingId);
  return recordingId;
};

// Get count of pending recordings — reads only metadata store, zero blobs loaded
export const getPendingCount = async () => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db
      .transaction([STORES.PENDING_RECORDINGS], 'readonly')
      .objectStore(STORES.PENDING_RECORDINGS)
      .getAll();
    req.onsuccess = () => resolve(req.result.filter(r => !r.synced).length);
    req.onerror = () => reject(req.error);
  });
};

// Get IDs + metadata of pending recordings — no blobs loaded at all
export const getPendingRecordingIds = async () => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db
      .transaction([STORES.PENDING_RECORDINGS], 'readonly')
      .objectStore(STORES.PENDING_RECORDINGS)
      .getAll();
    req.onsuccess = () => resolve(
      req.result
        .filter(r => !r.synced)
        .map(({ id, trackingId, duration, startTime, timestamp }) => ({ id, trackingId, duration, startTime, timestamp }))
    );
    req.onerror = () => reject(req.error);
  });
};

// Get a single recording with its blob (loads only ONE blob at a time)
export const getRecordingById = async (id) => {
  await initDB();

  const meta = await new Promise((resolve, reject) => {
    const req = db.transaction([STORES.PENDING_RECORDINGS], 'readonly').objectStore(STORES.PENDING_RECORDINGS).get(id);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  if (!meta) return null;

  // Try to get blob from separate store (v2), fall back to inline blob (v1 records)
  const blobRecord = await new Promise((resolve) => {
    const req = db.transaction([STORES.PENDING_BLOBS], 'readonly').objectStore(STORES.PENDING_BLOBS).get(id);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });

  return { ...meta, videoBlob: blobRecord?.videoBlob ?? meta.videoBlob };
};

// Get all pending recordings (not yet synced) — legacy, loads blobs
export const getPendingRecordings = async () => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction([STORES.PENDING_RECORDINGS], 'readonly').objectStore(STORES.PENDING_RECORDINGS).getAll();
    req.onsuccess = () => resolve(req.result.filter(r => !r.synced));
    req.onerror = () => reject(req.error);
  });
};

// Mark recording as synced
export const markRecordingSynced = async (id) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const store = db.transaction([STORES.PENDING_RECORDINGS], 'readwrite').objectStore(STORES.PENDING_RECORDINGS);
    const get = store.get(id);
    get.onsuccess = () => {
      const rec = get.result;
      if (rec) {
        rec.synced = true;
        rec.syncedAt = Date.now();
        const put = store.put(rec);
        put.onsuccess = () => resolve();
        put.onerror = () => reject(put.error);
      } else {
        resolve();
      }
    };
    get.onerror = () => reject(get.error);
  });
};

// Delete recording from both stores
export const deletePendingRecording = async (id) => {
  await initDB();
  await new Promise((resolve, reject) => {
    const req = db.transaction([STORES.PENDING_RECORDINGS], 'readwrite').objectStore(STORES.PENDING_RECORDINGS).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  // Delete blob (ignore error if not found — old v1 record won't have it)
  await new Promise((resolve) => {
    const req = db.transaction([STORES.PENDING_BLOBS], 'readwrite').objectStore(STORES.PENDING_BLOBS).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
};

// ─── Cache ────────────────────────────────────────────────────────────────────

export const cacheData = async (key, data) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction([STORES.CACHED_DATA], 'readwrite').objectStore(STORES.CACHED_DATA).put({ key, data, timestamp: Date.now() });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

export const getCachedData = async (key) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction([STORES.CACHED_DATA], 'readonly').objectStore(STORES.CACHED_DATA).get(key);
    req.onsuccess = () => resolve(req.result ? req.result.data : null);
    req.onerror = () => reject(req.error);
  });
};

export const getOfflineQueueCount = async () => {
  await initDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction([STORES.OFFLINE_QUEUE], 'readonly').objectStore(STORES.OFFLINE_QUEUE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

export const getPendingRecordingsCount = getPendingCount;

export default {
  initDB,
  addToOfflineQueue,
  getOfflineQueue,
  removeFromOfflineQueue,
  clearOfflineQueue,
  savePendingRecording,
  getPendingRecordings,
  getPendingCount,
  getPendingRecordingIds,
  getRecordingById,
  markRecordingSynced,
  deletePendingRecording,
  cacheData,
  getCachedData,
  getOfflineQueueCount,
  getPendingRecordingsCount,
};
